import React, { useState, useMemo } from 'react';
import { Button, Typography } from 'antd';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import * as yup from 'yup';
// Hooks
import { useReviewDeliverables } from 'hooks/useReviewDeliverables';
// Types
import {
  GetCampaignOffers_getCampaignOffers_entities_deliverablesV2,
  GetCampaignOffers_getCampaignOffers_entities,
  GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items,
} from 'api/campaignV2/types/GetCampaignOffers';
import {
  DeliverableReviewAction,
  CBDeliverableItemType,
  CbDeliverableItemV2Status,
  CampaignTaskType,
} from 'api/graphql-global-types';
// Helpers
import {
  getDeliverableKind,
  isContentOnlyDeliverable,
} from 'helpers/deliverables';
// UI
import { errorNotification, successNotification } from 'ui/Notification';
// Components
import ContentReviewSection from './components/ContentReviewSection/ContentReviewSection';
import CaptionReviewSection from './components/CaptionReviewSection/CaptionReviewSection';
import AppearanceReviewSection from './components/AppearanceReviewSection/AppearanceReviewSection';
import RejectFeedbackModal from './components/RejectFeedbackModal/RejectFeedbackModal';
import RevisionHistoryModal from './components/RevisionHistoryModal/RevisionHistoryModal';
import FinalEditReviewSection from './components/FinalEditReviewSection/FinalEditReviewSection';
import RejectFinalEditModal from './components/RejectFinalEditModal/RejectFinalEditModal';
// Styles
import styles from './TimelineDeliverablesReviewForm.module.scss';

const { Text, Title } = Typography;

type Review = {
  deliverableItemId: string;
  action: DeliverableReviewAction | null;
  feedback?: string;
  alreadyReviewed?: boolean;
};

export type DeliverableReviewData = {
  finalReelReview?: Review;
  contentReviews: Review[];
  postReview: Review;
};

type TimelineDeliverablesReviewFormProps = {
  deliverable: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2;
  offer: GetCampaignOffers_getCampaignOffers_entities;
  onClose: () => void;
  refetchOffers: () => void;
};

export const ALREADY_REVIEWED_STATUSES = [
  CbDeliverableItemV2Status.APPROVED,
  CbDeliverableItemV2Status.REJECTED,
];

const getDeliverableReviewValidation = (contentOnly: boolean) =>
  yup.object().shape({
    finalReelReview: yup
      .object()
      .shape({
        deliverableItemId: yup.string().optional(),
        action: yup.string().nullable().optional(),
        feedback: yup.string().optional(),
      })
      .optional(),
    contentReviews: yup.array().of(
      yup.object().shape({
        deliverableItemId: yup
          .string()
          .required('Deliverable item ID is required'),
        action: yup.string().nullable().required('Action is required'),
        feedback: yup.string().optional(),
      })
    ),
    postReview: contentOnly
      ? yup.object().shape({
          deliverableItemId: yup.string().optional(),
          action: yup.string().nullable().optional(),
          feedback: yup.string().optional(),
        })
      : yup.object().shape({
          deliverableItemId: yup
            .string()
            .required('Deliverable item ID is required'),
          action: yup.string().nullable().required('Action is required'),
          feedback: yup.string().optional(),
        }),
  });

const mapItemStatusToAction = (status: CbDeliverableItemV2Status) => {
  switch (status) {
    case CbDeliverableItemV2Status.APPROVED:
      return DeliverableReviewAction.APPROVE;
    case CbDeliverableItemV2Status.REJECTED:
      return DeliverableReviewAction.REJECT;
    default:
      return null;
  }
};

const TimelineDeliverablesReviewForm: React.FC<
  TimelineDeliverablesReviewFormProps
> = ({ deliverable, offer, onClose, refetchOffers }) => {
  const isAppearance = getDeliverableKind(deliverable) === 'appearance';
  const contentOnly = isContentOnlyDeliverable(
    offer.campaign.type,
    deliverable
  );

  const [rejectItem, setRejectItem] =
    useState<GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items | null>(
      null
    );
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showRevisionHistory, setShowRevisionHistory] = useState(false);
  const [showRejectFinalEditModal, setShowRejectFinalEditModal] =
    useState(false);

  const { reviewDeliverables } = useReviewDeliverables();

  // Split content and captions
  const deliverableItemsArray = deliverable?.items;
  const deliverableItems = useMemo(
    () => deliverableItemsArray || [],
    [deliverableItemsArray]
  );

  const finalReelItem = useMemo(
    () =>
      deliverableItems.find(
        (item) => item.task?.type === CampaignTaskType.FINAL_CONTENT
      ) || null,
    [deliverableItems]
  );
  const hasFinalReel = useMemo(
    () =>
      finalReelItem !== null &&
      finalReelItem.status !== CbDeliverableItemV2Status.NOT_SUBMITTED,
    [finalReelItem]
  );

  const contentItems = useMemo(
    () =>
      deliverableItems.filter(
        (item) =>
          item.type === CBDeliverableItemType.FILE &&
          item.task?.type !== CampaignTaskType.FINAL_CONTENT
      ),
    [deliverableItems]
  );
  const captionItems = useMemo(
    () =>
      deliverableItems.filter(
        (item) => item.type === CBDeliverableItemType.COPY
      ),
    [deliverableItems]
  );

  const initialValues = useMemo(() => {
    return {
      ...(hasFinalReel && finalReelItem
        ? {
            finalReelReview: {
              deliverableItemId: finalReelItem.id,
              action: mapItemStatusToAction(finalReelItem.status),
              alreadyReviewed: ALREADY_REVIEWED_STATUSES.includes(
                finalReelItem.status
              ),
            },
          }
        : {}),
      contentReviews: contentItems.map((item) => ({
        deliverableItemId: item.id,
        action: mapItemStatusToAction(item.status),
        alreadyReviewed: ALREADY_REVIEWED_STATUSES.includes(item.status),
      })),
      postReview: {
        deliverableItemId: captionItems[0]?.id || '',
        action: mapItemStatusToAction(captionItems[0]?.status),
        alreadyReviewed: ALREADY_REVIEWED_STATUSES.includes(
          captionItems[0]?.status
        ),
        feedback: '',
      },
    };
  }, [contentItems, captionItems, hasFinalReel, finalReelItem]);

  const methods = useForm<DeliverableReviewData>({
    resolver: yupResolver(getDeliverableReviewValidation(contentOnly)),
    defaultValues: initialValues,
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldUnregister: false,
  });

  const watchedFinalReelAction = methods.watch('finalReelReview.action');
  const finalReelApproved =
    watchedFinalReelAction === DeliverableReviewAction.APPROVE;

  // Check if all items have been reviewed
  const isAllItemsReviewed = useMemo(() => {
    const watchedValues = methods.watch();

    // Check content items
    const allContentReviewed =
      watchedValues.contentReviews?.every((review) => review.action !== null) ??
      true;

    // Skip caption check for content-only (UGC) campaigns
    if (contentOnly) {
      return allContentReviewed;
    }

    // Check caption items (post review)
    const captionReviewed = watchedValues.postReview?.action !== null;

    return allContentReviewed && captionReviewed;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methods.watch()]);

  // Check if there are any rejected items that need revision
  const hasRejectedItems = useMemo(() => {
    return (
      contentItems.some(
        (item) => item.status === CbDeliverableItemV2Status.REJECTED
      ) ||
      captionItems.some(
        (item) => item.status === CbDeliverableItemV2Status.REJECTED
      )
    );
  }, [contentItems, captionItems]);

  // Check if there's revision history available
  const hasRevisionHistory =
    deliverable.revisions && deliverable.revisions.length > 0;

  const handleFormSubmit = async (data: DeliverableReviewData) => {
    const reviewDataInput = {
      offerId: offer.id,
      deliverableItems: [] as Array<{
        deliverableItemId: string;
        action: DeliverableReviewAction;
        feedback: string | null;
      }>,
    };

    // Review final reel item
    if (
      data.finalReelReview &&
      !data.finalReelReview.alreadyReviewed &&
      data.finalReelReview.action
    ) {
      reviewDataInput.deliverableItems.push({
        deliverableItemId: data.finalReelReview.deliverableItemId,
        action: data.finalReelReview.action,
        feedback: data.finalReelReview.feedback || null,
      });
    }

    // Review content items
    if (data.contentReviews.length > 0) {
      const contentReviews = data.contentReviews
        .map((review) => {
          if (!review.alreadyReviewed && review.action) {
            return {
              deliverableItemId: review.deliverableItemId,
              action: review.action,
              feedback: review.feedback || null,
            };
          }
          return null;
        })
        .filter((item): item is NonNullable<typeof item> => item !== null);
      reviewDataInput.deliverableItems.push(...contentReviews);
    }

    if (
      Object.keys(data.postReview).length > 0 &&
      !data.postReview.alreadyReviewed &&
      data.postReview.action
    ) {
      reviewDataInput.deliverableItems.push({
        deliverableItemId: data.postReview.deliverableItemId,
        action: data.postReview.action,
        feedback: data.postReview.feedback || null,
      });
    }

    try {
      await reviewDeliverables({
        variables: {
          input: reviewDataInput,
        },
      });
      successNotification('Deliverable review submitted successfully');
      onClose();
      refetchOffers();
    } catch (error: any) {
      errorNotification(
        error?.message || 'Something went wrong, please try again later'
      );
    }
  };

  const handleRejectFeedback = (
    item: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items
  ) => {
    setRejectItem(item);
    setShowRejectModal(true);
  };

  const handleRejectModalClose = () => {
    setShowRejectModal(false);
    setRejectItem(null);
  };

  const handleShowRevisionHistory = () => {
    setShowRevisionHistory(true);
  };

  const handleHideRevisionHistory = () => {
    setShowRevisionHistory(false);
  };

  // Final Edited Reel: approve → auto-approve all content + post copy
  const handleApproveFinalReel = () => {
    const currentReviews = methods.getValues('contentReviews');
    const updatedReviews = currentReviews.map((review) => {
      if (!review.alreadyReviewed) {
        return { ...review, action: DeliverableReviewAction.APPROVE };
      }
      return review;
    });
    methods.setValue('contentReviews', updatedReviews);
  };

  // Final Edited Reel: unapprove → reset
  const handleUnapproveFinalReel = () => {
    const currentReviews = methods.getValues('contentReviews');
    const resetReviews = currentReviews.map((review) => {
      if (!review.alreadyReviewed) {
        return { ...review, action: null };
      }
      return review;
    });
    methods.setValue('contentReviews', resetReviews);
  };

  // Final Edited Reel: reject → show RejectFinalEditModal
  const handleRejectFinalReel = () => {
    setShowRejectFinalEditModal(true);
  };

  // RejectFinalEditModal: "Submit Edits" — reject only final edit
  const handleSubmitEdits = async (feedback: string) => {
    if (!finalReelItem) return;
    try {
      await reviewDeliverables({
        variables: {
          input: {
            offerId: offer.id,
            deliverableItems: [
              {
                deliverableItemId: finalReelItem.id,
                action: DeliverableReviewAction.REJECT,
                feedback,
              },
            ],
          },
        },
      });
      successNotification('Final edit feedback submitted successfully');
      methods.setValue('finalReelReview', {
        deliverableItemId: finalReelItem.id,
        action: DeliverableReviewAction.REJECT,
        alreadyReviewed: true,
      });
      setShowRejectFinalEditModal(false);
      refetchOffers();
    } catch (error: any) {
      errorNotification(
        error?.message || 'Something went wrong, please try again later'
      );
    }
  };

  // RejectFinalEditModal: "Request Re-Shoots" — reject all
  const handleRequestReshoots = async (feedback: string) => {
    if (!finalReelItem) return;
    const deliverableItemsPayload = [
      {
        deliverableItemId: finalReelItem.id,
        action: DeliverableReviewAction.REJECT,
        feedback,
      },
      ...contentItems
        .filter((it) => !ALREADY_REVIEWED_STATUSES.includes(it.status))
        .map((it) => ({
          deliverableItemId: it.id,
          action: DeliverableReviewAction.REJECT,
          feedback: 'Re-shoots required on content',
        })),
      ...captionItems
        .filter((it) => !ALREADY_REVIEWED_STATUSES.includes(it.status))
        .map((it) => ({
          deliverableItemId: it.id,
          action: DeliverableReviewAction.REJECT,
          feedback: 'Re-shoots required on content',
        })),
    ];
    try {
      await reviewDeliverables({
        variables: {
          input: {
            offerId: offer.id,
            deliverableItems: deliverableItemsPayload,
          },
        },
      });
      successNotification('Re-shoot request submitted successfully');
      setShowRejectFinalEditModal(false);
      onClose();
      refetchOffers();
    } catch (error: any) {
      errorNotification(
        error?.message || 'Something went wrong, please try again later'
      );
    }
  };

  // Appearance deliverables have no content/post caption — they are verified
  // (approve-only) via their confirmation item, matching the talent-facing flow.
  if (isAppearance) {
    return (
      <div className={styles.root}>
        <div className={styles.header}>
          <Title level={4}>Review Deliverable</Title>
          <Text type="secondary">
            {deliverable.task?.title || 'Deliverable'}
          </Text>
        </div>
        <AppearanceReviewSection
          deliverable={deliverable}
          offer={offer}
          onClose={onClose}
          refetchOffers={refetchOffers}
        />
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleFormSubmit)}>
          <div className={styles.header}>
            <Title level={4}>Review Deliverable</Title>
            <Text type="secondary">
              {deliverable.task?.title || 'Deliverable'}
            </Text>
          </div>

          {/* Show feedback banner if there are rejected items */}
          {hasRejectedItems && (
            <div className={styles.feedbackBanner}>
              <div className={styles.feedbackBannerContent}>
                <div className={styles.feedbackBannerText}>
                  <Title level={5} className={styles.feedbackBannerTitle}>
                    Brand&apos;s Feedback
                  </Title>
                  <Text type="secondary">
                    Review changes and feedback before submitting your review.
                  </Text>
                </div>
                {hasRevisionHistory && (
                  <Button
                    type="primary"
                    size="small"
                    onClick={handleShowRevisionHistory}
                  >
                    See Feedback
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Final Edit Section - shown when FINAL_CONTENT exists */}
          {hasFinalReel && finalReelItem && !finalReelApproved && (
            <div className={styles.finalEditSection}>
              <FinalEditReviewSection
                finalReelItem={finalReelItem}
                onApproveAll={handleApproveFinalReel}
                onUnapprove={handleUnapproveFinalReel}
                onRejectClick={handleRejectFinalReel}
              />
            </div>
          )}

          {finalReelApproved && (
            <div className={styles.approvedBanner}>
              <Title level={5}>Content auto-approved with final edit</Title>
            </div>
          )}

          {!finalReelApproved && (
            <div className={styles.splitContainer}>
              {/* Left Side - Content */}
              <div className={contentOnly ? undefined : styles.leftSection}>
                <ContentReviewSection
                  contentItems={contentItems}
                  onRejectClick={handleRejectFeedback}
                />
              </div>
            </div>
          )}

          {/* Caption - always shown separately, not auto-approved with final reel */}
          {!contentOnly && (
            <div className={styles.splitContainer}>
              <div className={styles.rightSection}>
                <CaptionReviewSection
                  captionItems={captionItems}
                  onRejectClick={handleRejectFeedback}
                />
              </div>
            </div>
          )}

          <div className={styles.actions}>
            <Button
              type="primary"
              onClick={() => methods.handleSubmit(handleFormSubmit)()}
              disabled={!isAllItemsReviewed}
            >
              Submit Review
            </Button>
          </div>
        </form>

        {/* Reject Feedback Modal - MOVED INSIDE FormProvider */}
        {showRejectModal && rejectItem && (
          <RejectFeedbackModal
            item={rejectItem}
            onClose={handleRejectModalClose}
          />
        )}

        {/* Revision History Modal */}
        {showRevisionHistory && (
          <RevisionHistoryModal
            deliverable={deliverable}
            isOpen={showRevisionHistory}
            onClose={handleHideRevisionHistory}
          />
        )}

        {/* Reject Final Edit Modal */}
        {showRejectFinalEditModal && (
          <RejectFinalEditModal
            visible={showRejectFinalEditModal}
            onBack={() => setShowRejectFinalEditModal(false)}
            onSubmitEdits={handleSubmitEdits}
            onRequestReshoots={handleRequestReshoots}
          />
        )}
      </FormProvider>
    </div>
  );
};

export default TimelineDeliverablesReviewForm;
